import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";
import { getState, pushWithModifier } from "../../helper/page.helper";
import { authSelector } from "../../redux/selectors/auth.selector";
import { D003HealthRecordSchema, D003Schema } from "../../generated";
import { initialD003Model } from "../../models/child.model";
import { fetchD003, fetchD003List, fetchD003ListCount } from "../../services/api.service";
import { D004Url, getForHistoryPush } from "../../helper/url.helper";
import { useLoadingIndicator } from "../../hooks/LoadingIndicator.hooks";
import ChildInfoTop from "../../components/ChildInfoTop/ChildInfoTop";
import CustomButton, { CB_CLASS } from "../../components/Button/CustomButton";
import Title from "../../components/Title/Title";
import LoadingSpinner from "../../components/Loading/LoadingSpinner";
import moment from "moment";
import { useAuth } from "../../helper/auth.helper";
import { notifyError } from "../../helper/settings.helper";
import PaginationTableLite from "../../components/PaginationTableLite/PaginationTableLite";
import { D001Url } from "../../helper/url.helper";

interface iD003 {
  child_id: number;
}

const D003 = () => {
  const { checkLoggedIn } = useAuth();
  const maxWidth = 900;
  const authState = useSelector(authSelector);
  const history = useHistory();
  const location = useLocation<iD003>();
  const state = getState(location);
  const childId = state.child_id;
  const [isLoading, , startLoading, stopLoading] = useLoadingIndicator();
  const [childData, setChildData] = useState<D003Schema>(initialD003Model);
  const [data, setData] = useState<D003HealthRecordSchema[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordCount, setRecordCount] = useState(0);
  const limitCount = 9;
  const moveByClick = (e: React.MouseEvent, routeData: any) => pushWithModifier(e, history, routeData);

  useEffect(() => {
    if (!childId) {
      notifyError("お子様が選択されていません。");
      history.replace(getForHistoryPush(D001Url, [0]));
    }
  }, [childId, history]);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      if (!childId) return;
      startLoading();
      try {
        const [resCount, resData] = await Promise.all([
          fetchD003ListCount(childId, authState),
          fetchD003List(childId, authState, (currentPage - 1) * limitCount, limitCount)
        ]);

        if (isMounted) {
          setRecordCount(resCount.data);
          setData(resData.data);
        }
        stopLoading();
      } catch (err) {
        if (isMounted) {
          checkLoggedIn(err);
          notifyError("データの取得に失敗しました。");
          console.error(err);
        }
        stopLoading();
      }
    };
    fetchData();
    return () => {
      isMounted = false;
    }
  }, [authState, checkLoggedIn, childId, currentPage, startLoading, stopLoading]);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      if (!childId) return;
      startLoading();
      try {
        const res = await fetchD003(childId, authState);
        if (isMounted) {
          setChildData(res.data);
        }
      } catch (err) {
        if (isMounted) {
          checkLoggedIn(err);
          notifyError("データの取得に失敗しました。");
          console.error(err);
        }
      } finally {
        stopLoading();
      }
    };
    fetchData();
    return () => {
      isMounted = false;
    }
  }, [authState, checkLoggedIn, childId, startLoading, stopLoading]);

  const changeCurrentPage = (v: number) => {
    setCurrentPage(v);
  };

  return (
    <>
      <LoadingSpinner isLoading={isLoading} />
      <Title title="身長・体重一覧" />
      <div className="plate">
        <div className="display_flex justify" style={{ maxWidth: maxWidth }}>
          <ChildInfoTop
            user_type={authState.userInfo?.user_type}
            profile_pic_url={childData.child_profile_pic_url}
            id={childId}
            name={childData.child_name}
            name_kana={childData.child_name_kana}
            facility_name={childData.facility_name}
            no_underbar={true}
          />
          <CustomButton
            label="＋身長・体重データを追加"
            class={[CB_CLASS.RED]}
            onClick={(e: React.MouseEvent) => moveByClick(e, getForHistoryPush(D004Url, [0, childId]))}
          />
        </div>
      </div>
      <div>
        <PaginationTableLite
          currentPage={currentPage}
          setCurrentPage={changeCurrentPage}
          recordCount={recordCount}
          limit={limitCount}
          headers={["日付", "身長", "体重"]}
          max_width={maxWidth}
          list={data.map((x, i) => (
            <tr key={i} onClick={(e: React.MouseEvent) => moveByClick(e, getForHistoryPush(D004Url, [x.id, childId]))}>
              <td className="item">{moment(x.measurement_date).format("YYYY/MM/DD")}</td>
              <td className="item">{x.height}</td>
              <td className="item">{x.weight}</td>
            </tr>
          ))}
        />
      </div>
    </>
  );
};

export default D003;
